export interface ConnectSourceResponse {
  connectSourceName: string;
  connectSourceCode: string;
}

export class EtriveConnectSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param {string} baseURL - The base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   * Wrapper for handling fetch requests and error handling
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} [options] - Request options
   * @returns {Promise<Response>} - Response or throws an error
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch (e) {
        // ignore
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Get Sources for tenant
   * @param {string} accessToken - user's access token
   * @returns {Promise<ConnectSourceResponse>} - Batch details
   */
  async getEtrieveSource(accessToken: string): Promise<ConnectSourceResponse> {
    let endpoint = `/etrieveConnect/sources`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * call source
   * @param {string} accessToken - user's access token
   * @param {string} sourceCode - source code for etrieve Connect Source
   * @param data
   * @returns {Promise<any>} - Send student Information to connect
   */
  async callSource(accessToken: string, sourceCode: string, data: any): Promise<any> {
    const endpoint = `/etrieveConnect/CallSource/${sourceCode}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Retrieves required fields for student and course information
   * @param {string} accessToken - user's access token
   * @returns {Promise<any>} - required student and course fields for client's SIS contract
   */
  async getRequiredFields(accessToken: string): Promise<any> {
    const endpoint = `/etrieveConnect/requiredfields`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.json();
  }
}
